import { _enum, array, boolean, number, object, schema, string } from "@anna-money/anna-web-lib/serializer";

export enum DIRECTOR_CH_VERIFICATION_STATUS {
  SUBMITTED = "SUBMITTED",
  FORM_SENT = "FORM_SENT",
  VERIFIED = "VERIFIED",
  FAILED = "FAILED",
  FAILED_WRONG_EMAIL = "FAILED_WRONG_EMAIL",
  /** ID check was completed with an identity already used by another person on the application */
  FAILED_DUPLICATED_IDENTITY = "FAILED_DUPLICATED_IDENTITY",
  /** Held only while a duplicate's ID check is being reset, unless that reset was interrupted */
  DEDUPLICATION_IN_PROGRESS = "DEDUPLICATION_IN_PROGRESS",
}

/**
 * Why web-forms-sender failed the director's ACSP identity verification form. The backend passes
 * the reason through as it came, so `identityVerificationFailReason` may hold a value not listed here.
 */
export enum ACSP_FAIL_REASON {
  WRONG_EMAIL = "WRONG_EMAIL",
  DUPLICATE_IDENTITY = "DUPLICATE_IDENTITY",
  DATA_MISMATCH = "DATA_MISMATCH",
  APPLE_PRIVATE_RELAY_EMAIL = "APPLE_PRIVATE_RELAY_EMAIL",
}

export class DirectorData extends schema(
  {
    id: string(),
    companyId: string(),
    created: string(),
    updated: string(),
    firstName: string().optional(),
    middleNames: string().optional(),
    lastName: string().optional(),
    dob: string().optional(),
    nationality: string().optional(),
    useRegisteredAddressAsService: boolean().optional(),
    ownsSharesNum: number().optional(),
    isDirector: boolean().optional(),
    isShareholder: boolean().optional(),
    isPersonWithSignificantControl: boolean().optional(),
    hasRightToAppointNewDirectors: boolean().optional(),
    hasSignificantControlOutsideOfShares: boolean().optional(),
    isCurrentAddressLessThanOneYear: boolean().nullish(),
    previousAddressInUk: boolean().nullish(),

    // AU
    countryOfBirth: string().optional(),
    suburbOfBirth: string().optional(),
    stateOfBirth: string().optional(),
    townOfBirth: string().optional(),
    tfn: string().optional(),
    email: string().optional(),
    mobile: string().optional(),
    formerFirstName: string().optional(),
    formerMiddleNames: string().optional(),
    formerLastName: string().optional(),
    drivingLicenceStateOfIssue: string().optional(),

    // ch validation fields
    chPersonalCode: string().nullish(),
    identityVerificationStatus: _enum(DIRECTOR_CH_VERIFICATION_STATUS, "DIRECTOR_CH_VERIFICATION_STATUS").optional(),
    identityVerificationFailReason: string().nullish(),
    identityVerificationFormId: string().optional(),
  },
  "DirectorData",
) {}

export class DirectorListData extends schema(
  {
    directors: array(object(DirectorData)),
  },
  "DirectorDataList",
) {}

export class MainDirectorCreateData extends schema(
  {
    companyId: string(),
  },
  "MainDirectorCreateData",
) {}

export class DirectorCreateData extends schema(
  {
    companyId: string(),
    firstName: string().optional(),
    middleNames: string().optional(),
    lastName: string().optional(),
    dob: string().optional(),
    nationality: string().optional(),
    isDirector: boolean().optional(),
    isShareholder: boolean().optional(),
    isPersonWithSignificantControl: boolean().optional(),
    email: string().optional(),
    mobile: string().optional(),
    // ch validation fields
    chPersonalCode: string().nullish(),
  },
  "DirectorCreateData",
) {}

export class MultiDirectorCreateData extends schema(
  {
    companyId: string(),
    firstName: string().optional(),
    middleNames: string().optional(),
    lastName: string().optional(),
    dob: string().optional(),
    nationality: string().optional(),
    isDirector: boolean().optional(),
    isShareholder: boolean().optional(),
    isPersonWithSignificantControl: boolean().optional(),
    // ch validation fields
    chPersonalCode: string().nullish(),

    // AU
    email: string().optional(),
    mobile: string().optional(),
    formerFirstName: string().optional(),
    formerMiddleNames: string().optional(),
    formerLastName: string().optional(),
    countryOfBirth: string().optional(),
    suburbOfBirth: string().optional(),
    stateOfBirth: string().optional(),
    townOfBirth: string().optional(),
    tfn: string().optional(),
  },
  "MultiDirectorCreateData",
) {}

export class DirectorUpdateData extends schema(
  {
    firstName: string().optional(),
    middleNames: string().optional(),
    lastName: string().optional(),
    dob: string().optional(),
    nationality: string().optional(),
    isDirector: boolean().optional(),
    isShareholder: boolean().optional(),
    isPersonWithSignificantControl: boolean().optional(),
    hasRightToAppointNewDirectors: boolean().optional(),
    hasSignificantControlOutsideOfShares: boolean().optional(),
    isCurrentAddressLessThanOneYear: boolean().nullish(),
    previousAddressInUk: boolean().nullish(),

    // ch validation fields
    chPersonalCode: string().nullish(),

    // AU
    email: string().optional(),
    mobile: string().optional(),
    formerFirstName: string().optional(),
    formerMiddleNames: string().optional(),
    formerLastName: string().optional(),
    countryOfBirth: string().optional(),
    suburbOfBirth: string().optional(),
    stateOfBirth: string().optional(),
    townOfBirth: string().optional(),
    tfn: string().optional(),
  },
  "DirectorUpdateData",
) {}

export class MultiDirectorUpdateData extends schema(
  {
    firstName: string().optional(),
    middleNames: string().optional(),
    lastName: string().optional(),
    dob: string().optional(),
    nationality: string().optional(),
    isDirector: boolean().optional(),
    isShareholder: boolean().optional(),
    isPersonWithSignificantControl: boolean().optional(),
    hasRightToAppointNewDirectors: boolean().optional(),
    hasSignificantControlOutsideOfShares: boolean().optional(),
    isCurrentAddressLessThanOneYear: boolean().nullish(),
    previousAddressInUk: boolean().nullish(),

    // AU

    email: string().optional(),
    mobile: string().optional(),
    formerFirstName: string().optional(),
    formerMiddleNames: string().optional(),
    formerLastName: string().optional(),
    countryOfBirth: string().optional(),
    suburbOfBirth: string().optional(),
    stateOfBirth: string().optional(),
    townOfBirth: string().optional(),
    tfn: string().optional(),
    // ch validation fields
    chPersonalCode: string().nullish(),
  },
  "MultiDirectorUpdateData",
) {}

export class DirectorSharesUpdateData extends schema(
  {
    ownsSharesNum: number().nullable(),
  },
  "DirectorSharesUpdateData",
) {}

export class DirectorSignificantUpdateData extends schema(
  {
    isPersonWithSignificantControl: boolean().nullish(),
    hasRightToAppointNewDirectors: boolean().nullish(),
    hasSignificantControlOutsideOfShares: boolean().nullish(),
  },
  "DirectorSignificantUpdateData",
) {}

export class DirectorPreviousAddressUpdateData extends schema(
  {
    isCurrentAddressLessThanOneYear: boolean().nullish(),
    previousAddressInUk: boolean().nullish(),
  },
  "DirectorPreviousAddressUpdateData",
) {}

export class DirectorDriversLicenceStateUpdateData extends schema(
  {
    drivingLicenceStateOfIssue: string(),
  },
  "DirectorDriversLicenceStateUpdateData",
) {}

export class DirectorCHPersonalCodeUpdateData extends schema(
  {
    chPersonalCode: string().nullish(),
  },
  "DirectorCHPersonalCodeUpdateData",
) {}
